import React, { useMemo, useState } from 'react';
import { Button, Card, Checkbox, Empty, Space, Tag, Typography } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { Lesson } from '../../api/types';
import { extractChallengeCards, SourceTask, StagedTask } from './migration';

const { Text, Title } = Typography;

interface Props {
  lessons: Lesson[];
  staged: StagedTask[];
  onStage: (added: StagedTask[]) => void;
}

const MigrationPanel: React.FC<Props> = ({ lessons, staged, onStage }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const stagedKeys = useMemo(
    () => new Set(staged.map((s) => s.origin?.key).filter(Boolean) as string[]),
    [staged],
  );
  const groups = useMemo(() => extractChallengeCards(lessons), [lessons]);

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  const moveSelected = () => {
    const chosen: SourceTask[] = groups
      .flatMap((g) => g.tasks)
      .filter((t) => selected.has(t.key) && !stagedKeys.has(t.key));

    onStage(chosen.map((origin) => ({
      task: { id: crypto.randomUUID(), title: origin.title, text: origin.text, tip: origin.tip },
      origin,
      keepInSource: false,
    })));
    setSelected(new Set());
  };

  const available = groups.flatMap((g) => g.tasks).filter((t) => !stagedKeys.has(t.key)).length;

  return (
    <Card
      title={`Found in module (${available})`}
      extra={
        <Button type="primary" icon={<ArrowRightOutlined />}
          disabled={selected.size === 0} onClick={moveSelected}>
          Move ({selected.size})
        </Button>
      }
    >
      {groups.length === 0 ? (
        <Empty description="No challenge cards left in this module's default lessons" />
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {groups.map((group) => (
            <div key={group.lessonId}>
              <Title level={5} style={{ marginBottom: 8 }}>
                {group.lessonTitle} <Tag>{group.tasks.length}</Tag>
              </Title>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {group.tasks.map((task) => {
                  const alreadyStaged = stagedKeys.has(task.key);
                  return (
                    <Card key={task.key} size="small" style={{ opacity: alreadyStaged ? 0.45 : 1 }}>
                      <Checkbox
                        checked={selected.has(task.key)}
                        disabled={alreadyStaged}
                        onChange={() => toggle(task.key)}
                      >
                        <Text strong>{task.title || '(no title)'}</Text>
                      </Checkbox>
                      <div style={{ marginLeft: 24, marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>{task.text}</Text>
                      </div>
                      {alreadyStaged && (
                        <div style={{ marginLeft: 24, marginTop: 4 }}><Tag color="green">staged</Tag></div>
                      )}
                    </Card>
                  );
                })}
              </Space>
            </div>
          ))}
        </Space>
      )}
    </Card>
  );
};

export default MigrationPanel;
